import { useState, useEffect, useContext } from 'react';
import { Circles } from 'react-loader-spinner';
import { ReactSVG } from 'react-svg';
import { Context } from '../Context';
import { getSublocations, updateSublocation } from './utils';
import { getLocations } from '../Locations/utils';
import SublocationDetail from './SublocationDetail';
import SublocationRules from './SublocationRules';
import SublocationModal from './SublocationModal';

import plusIcon from '../../images/icons/screens/white-plus.svg';
import saveIcon from '../../images/icons/menu/save.svg';

const JourneyPage = ({ lang, userData, customer }) => {
    const [state] = useContext(Context);
    userData = state.userData;
    customer = state.customer;

    const [sublocations, setSublocations] = useState([]);
    const [locations, setLocations] = useState([]);
    const [editSublocation, setEditSublocation] = useState(null);
    const [isEdited, setIsEdited] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [sortConfig, setSortConfig] = useState({ sortBy: 'name', direction: 'asc' });

    useEffect(() => {
        fetchSublocations();
        getLocations(customer.id).then(data => {
            if (Array.isArray(data)) setLocations(data);
        });
    }, []);

    const fetchSublocations = async () => {
        const data = await getSublocations(customer.id);
        if (Array.isArray(data)) setSublocations(data);
    };

    const handleRowClick = (sublocation) => {
        setEditSublocation(sublocation);
        setIsEdited(false);
    };

    // Called when AddressField selects a result — updates the whole sublocation object at once
    const handleAddressSelect = (updatedSublocation) => {
        setEditSublocation(updatedSublocation);
        setIsEdited(true);
    };

    const handleFieldChange = (field, value) => {
        setEditSublocation(prev => ({ ...prev, [field]: value }));
        setIsEdited(true);
    };

    const handleSort = (column) => {
        setSortConfig(prev => ({
            sortBy: column,
            direction: prev.sortBy === column && prev.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    const sortedSublocations = [...sublocations].sort((a, b) => {
        const aVal = a[sortConfig.sortBy] ?? '';
        const bVal = b[sortConfig.sortBy] ?? '';
        const cmp = typeof aVal === 'boolean'
            ? (aVal === bVal ? 0 : aVal ? -1 : 1)
            : String(aVal).localeCompare(String(bVal));
        return sortConfig.direction === 'asc' ? cmp : -cmp;
    });

    const handleSubmit = async () => {
        if (!editSublocation || !isEdited) return;
        setLoading(true);
        try {
            await updateSublocation(editSublocation);
            await fetchSublocations();
            setIsEdited(false);
        } catch (e) {
            console.error('Failed to save sublocation', e);
        }
        setLoading(false);
    };

    // Called by SublocationModal after a successful create
    const handleCreated = (newSublocation) => {
        setSublocations(prev => [...prev, newSublocation]);
        setEditSublocation(newSublocation);
    };

    const fillSvgColor = (svg, fill) => {
        for (let i = 0; i < svg.children.length; i++) {
            svg.children[i].setAttribute('fill', fill);
        }
    };

    const formatDate = (dt) => {
        if (!dt) return '';
        const d = new Date(dt);
        return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
    };

    return (
        <div className="JourneyPage">
            {loading && <LoadingModal />}

            {showModal && (
                <SublocationModal
                    locations={locations}
                    onCreated={handleCreated}
                    onClose={() => setShowModal(false)}
                />
            )}

            <div className="actions-tab">
                <div className="left-container">
                    <button className="open-location pure-u-1-9" onClick={() => setShowModal(true)}>
                        <img className="open-icon" src={plusIcon} alt="add" />
                    </button>
                    <div className="location-name pure-u-1-7">
                        <p style={{ marginTop: 0, marginBottom: 0 }}>{customer?.name}</p>
                        <p style={{ marginTop: '0.5em' }}>{customer?.address1}</p>
                    </div>
                </div>
                <div
                    className={`icon-container ${isEdited ? 'changed' : ''}`}
                    title="Save changes"
                    onClick={handleSubmit}
                >
                    <ReactSVG
                        className="icon"
                        src={saveIcon}
                        beforeInjection={(svg) => isEdited ? fillSvgColor(svg, '#fff') : null}
                    />
                </div>
            </div>

            <div className="locations-container">
                <h3>Sublocations</h3>
                <div className="locations-table-wrapper">
                    <table className="locations-table">
                        <thead>
                            <tr className="header-row">
                                <th className="table-header" onClick={() => handleSort('name')}>Name</th>
                                <th className="table-header" onClick={() => handleSort('address1')}>Address</th>
                                <th className="table-header" onClick={() => handleSort('city')}>City</th>
                                <th className="table-header" onClick={() => handleSort('locationId')}>Location</th>
                                <th className="table-header" onClick={() => handleSort('isPreset')}>Preset</th>
                                <th className="table-header" onClick={() => handleSort('isFinal')}>Final</th>
                                <th className="table-header" onClick={() => handleSort('createdAt')}>Created</th>
                            </tr>
                        </thead>
                        <tbody className="table-body">
                            {sortedSublocations.length === 0
                                ? <tr className="no-locations-text"><td colSpan={7}>No sublocations found</td></tr>
                                : sortedSublocations.map(s => (
                                    <tr
                                        key={s.id}
                                        className={`location-row ${editSublocation?.id === s.id ? 'selected' : ''}`}
                                        onClick={() => handleRowClick(s)}
                                    >
                                        <td>{s.name}</td>
                                        <td>{s.address1}</td>
                                        <td>{s.city}</td>
                                        <td>{locations.find(l => l.id === s.locationId)?.name ?? s.locationId}</td>
                                        <td>{s.isPreset ? '✓' : ''}</td>
                                        <td>{s.isFinal ? '✓' : ''}</td>
                                        <td>{formatDate(s.createdAt)}</td>
                                    </tr>
                                ))
                            }
                        </tbody>
                    </table>
                </div>
            </div>

            {editSublocation && (
                <>
                    <SublocationDetail
                        sublocation={editSublocation}
                        locations={locations}
                        onChange={handleFieldChange}
                        onAddressSelect={handleAddressSelect}
                    />
                    {editSublocation.id && (
                        <SublocationRules sublocationId={editSublocation.id} />
                    )}
                </>
            )}
        </div>
    );
};

const LoadingModal = () => (
    <div className="Loading">
        <Circles height="80" width="80" color="#2386EE" ariaLabel="circles-loading" visible={true} />
    </div>
);

export default JourneyPage;
